import { useEffect, useRef } from 'react'
import { useVaultStore } from '@/stores/useVaultStore'

/**
 * Keeps a /vault view fresh against server-side changes (new blocks from the
 * watcher, Concierge edits, calendar status flips) WITHOUT a manual reload.
 *
 * Calls `fn` on a background cadence:
 *   - every `intervalMs` (default 30s), and
 *   - when the window/tab regains focus (debounced against the interval).
 *
 * Skips a tick while the tab is hidden (no point refetching unseen UI) or while
 * a card is being actively edited (`store.editing`) — so a refetch can never
 * disturb in-flight typing. `fn` MUST update state silently (no loading flash):
 * the whole point is that the view updates in place, not that it blinks.
 */
export function useAutoRefresh(fn: () => void, intervalMs = 30000) {
  const fnRef = useRef(fn)
  fnRef.current = fn
  const lastRun = useRef(Date.now())

  useEffect(() => {
    const run = () => {
      if (document.hidden) return
      if (useVaultStore.getState().editing) return
      lastRun.current = Date.now()
      fnRef.current()
    }
    const id = window.setInterval(run, intervalMs)
    // Refresh on focus, but only if it's been a few seconds — avoids a
    // double-fetch when a focus event and the interval coincide.
    const onFocus = () => {
      if (Date.now() - lastRun.current > 5000) run()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [intervalMs])
}
